import {ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip} from "@mui/material";
import * as React from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "~/stores";
import type {RouteKey} from "~/routing/routes.ts";
import {useLocation, useNavigate, useParams} from "react-router";
import {tryGeneratePath} from "~/routing/routing.utils.ts";
import type {DashboardCard} from "../../dashboard/DashboardCardConfigs.tsx";
import {CARD_CONFIG} from "../../dashboard/DashboardCardConfigs.tsx";
import HomeIcon from "../../icons/svg/HomeIcon.svg?react";
import {environment} from "~/env.ts";
import {MLink} from "../../_tiny/MLink.tsx";
import {useChatAvailableForActiveDatabag} from "~/pages/chat-agent/hooks/useChatAvailableForActiveDatabag.ts";
import {CHAT_UNAVAILABLE_DATABAG_TOOLTIP} from "~/pages/chat-agent/utils/agentDatabag.ts";

type Props = {
    card: DashboardCard | "__home__";
};
export const DrawerMenuItem: React.FC<Props> = observer(({card}) => {
    const {p, rootAppStore, bagStore} = useStores();
    const chatDatabagAvailability = useChatAvailableForActiveDatabag();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    let icon: React.ReactNode;
    let title: React.ReactNode;
    let route: false | RouteKey | Array<RouteKey>;
    let disabled = false;
    let tooltip: React.ReactNode = null;
    let routePrefix: string | undefined;

    if (card === "__home__") {
        icon = <HomeIcon/>;
        title = "Home";
        route = ["homepage_dashboard", "home"];
    } else {
        if (!p.showCard(card)) {
            return null;
        }
        const config = CARD_CONFIG[card];
        icon = config.icon;
        title = config.title;
        route =
            environment.package === "sales_demo" && config.demoRoute
                ? config.demoRoute
                : config.route;
        routePrefix = config.routePrefix;
        const isDisabled = p.isDisabledCard(card, bagStore.bag);
        disabled = isDisabled.disabled;
        tooltip = isDisabled.message;

        if (
            card === "chat" &&
            chatDatabagAvailability.isReady &&
            !chatDatabagAvailability.isAvailable
        ) {
            disabled = true;
            tooltip = CHAT_UNAVAILABLE_DATABAG_TOOLTIP;
        }
    }
    if (route === undefined) {
        console.error("DrawerMenuItem: route is undefined");
        return null;
    }
    const open = rootAppStore.isDrawerOpen;
    const path = tryGeneratePath(params, route, bagStore.bag?.id);
    disabled = path === null || disabled;

    return (
        <ListItem disablePadding sx={{display: "block"}}>
            <Tooltip title={tooltip} placement="right">
                <span>
                    <MLink path={path} disabled={disabled}>
                        <ListItemButton
                            disabled={disabled}
                            selected={(location.pathname === path) || (routePrefix !== undefined && location.pathname.startsWith(routePrefix))}
                            sx={
                                open
                                    ? {justifyContent: "initial"}
                                    : {justifyContent: "center"}
                            }
                        >
                            <ListItemIcon
                                sx={[
                                    {minWidth: 0, justifyContent: "center"},
                                    open ? {} : {mr: 0},
                                ]}
                            >
                                {icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={title}
                                sx={[open ? {opacity: 1} : {opacity: 0}]}
                            />
                        </ListItemButton>
                    </MLink>
                </span>
            </Tooltip>
        </ListItem>
    );
});
